import { useState } from 'react';
import { 
  Copy, Check, ExternalLink, AlertTriangle, 
  AlertCircle, Info, TrendingUp, TrendingDown, Minus, Globe, Search, MapPin 
} from 'lucide-react';

// --- Utility Components ---

const SeverityBadge = ({ severity }: { severity: string }) => {
  const styles = {
    high: 'bg-red-500/10 text-red-400 border-red-500/20',
    medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    low: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };
  const icons = {
    high: <AlertTriangle className="h-3 w-3 mr-1" />,
    medium: <AlertCircle className="h-3 w-3 mr-1" />,
    low: <Info className="h-3 w-3 mr-1" />,
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${styles[severity as keyof typeof styles] || styles.low}`}>
      {icons[severity as keyof typeof icons]}
      {severity}
    </span>
  );
};

const ScoreBadge = ({ score, label }: { score: number; label?: string }) => {
  let color = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (score < 40) color = 'bg-red-500/10 text-red-400 border-red-500/20';
  else if (score < 70) color = 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';

  return (
    <div className={`flex flex-col items-center justify-center p-3 rounded-lg border ${color}`}>
      <span className="text-2xl font-bold">{score}</span>
      {label && <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</span>}
    </div>
  );
};

const CopyButton = ({ data }: { data: any }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-md transition-all border border-slate-700 hover:border-slate-600 active:scale-95"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy JSON'}
    </button>
  );
};

// --- Block Specific Renderers ---

export const SeoAuditRenderer = ({ data }: { data: any }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <Globe className="h-5 w-5 text-blue-400" /> SEO Audit
        </h3>
        <a href={data.base_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline flex items-center gap-1 mt-1">
          {data.base_url} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="flex items-center gap-4">
        <ScoreBadge score={data.overall_score} label={data.overall_label} />
        <div className="text-right">
          <div className="text-2xl font-bold text-slate-200">{data.pages_crawled}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Pages Crawled</div>
        </div>
      </div>
    </div>

    {data.dimension_scores && (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {data.dimension_scores.map((dim: any, idx: number) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-slate-200">{dim.score}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{dim.dimension.replace('_', ' ')}</div>
          </div>
        ))}
      </div>
    )}

    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Page Details</h4>
      {data.pages?.map((page: any, idx: number) => (
        <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
            <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-400 hover:underline break-all flex items-center gap-1">
              {page.url} <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            <div className="flex items-center gap-2 text-xs text-slate-400 shrink-0">
              <span>{page.word_count} words</span>
              <span>•</span>
              <span>{page.h1_count} H1</span>
              <span>•</span>
              <span className={page.is_indexable ? 'text-emerald-400' : 'text-red-400'}>
                {page.is_indexable ? 'Indexable' : 'Noindex'}
              </span>
            </div>
          </div>
          {page.issues && page.issues.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-slate-800/50">
              {page.issues.map((issue: any, i: number) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <SeverityBadge severity={issue.severity} />
                  <span className="text-slate-300 flex-1">{issue.detail || issue.code}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
    <div className="flex justify-end pt-2">
      <CopyButton data={data} />
    </div>
  </div>
);

export const KeywordSearchRenderer = ({ data }: { data: any }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
        <Search className="h-5 w-5 text-emerald-400" /> Keyword Rankings
      </h3>
      <CopyButton data={data} />
    </div>
    
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm text-left">
        <thead className="text-xs text-slate-500 uppercase bg-slate-900/80">
          <tr>
            <th className="px-4 py-3">Keyword</th>
            <th className="px-4 py-3">Intent</th>
            <th className="px-4 py-3 text-center">Vol</th>
            <th className="px-4 py-3 text-center">Org Rank</th>
            <th className="px-4 py-3 text-center">Local Rank</th>
            <th className="px-4 py-3 text-center">Trend</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800 bg-slate-900/30">
          {data.keywords?.map((kw: any, idx: number) => {
            const latest = kw.history?.[0] || {};
            const trendIcon = kw.trend_direction === 'up' ? <TrendingUp className="h-4 w-4 text-emerald-400" /> : 
                              kw.trend_direction === 'down' ? <TrendingDown className="h-4 w-4 text-red-400" /> : 
                              <Minus className="h-4 w-4 text-slate-500" />;
            return (
              <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-200">{kw.keyword_text}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-800 text-slate-300 border border-slate-700">
                    {kw.intent || 'N/A'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-slate-400">{kw.monthly_volume || '-'}</td>
                <td className="px-4 py-3 text-center">
                  {latest.organic_position ? (
                    <span className={`font-bold ${latest.organic_position <= 3 ? 'text-emerald-400' : 'text-slate-200'}`}>
                      #{latest.organic_position}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  {latest.local_pack_position ? (
                    <span className={`font-bold ${latest.local_pack_position <= 3 ? 'text-emerald-400' : 'text-slate-200'}`}>
                      #{latest.local_pack_position}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-center flex justify-center">{trendIcon}</td>
              </tr>
            );
          })}
          {(!data.keywords || data.keywords.length === 0) && (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No keywords tracked yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export const KeywordGenerationRenderer = ({ data }: { data: any }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
        <Search className="h-5 w-5 text-purple-400" /> Generated Keywords
      </h3>
      <CopyButton data={data} />
    </div>
    <div className="grid gap-3">
      {data.keywords?.map((kw: any, idx: number) => (
        <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-semibold text-slate-200">{kw.keyword_text}</span>
            {kw.is_branded && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">Branded</span>}
            {kw.is_local_intent && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Local</span>}
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-800 text-slate-300 border border-slate-700">{kw.intent}</span>
          </div>
          <p className="text-xs text-slate-400 italic">"{kw.rationale}"</p>
        </div>
      ))}
    </div>
  </div>
);

export const GridSearchRenderer = ({ data }: { data: any }) => (
  <div className="space-y-4">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-orange-400" /> Grid Search
        </h3>
        <p className="text-sm text-slate-400 mt-1">
          Keyword: <span className="text-slate-200 font-medium">"{data.keyword_text}"</span> • 
          Grid: {data.grid_size}x{data.grid_size} • Radius: {data.radius_km}km
        </p>
      </div>
      <div className="flex items-center gap-4">
        <ScoreBadge score={data.overall_visibility} label="Visibility" />
        <CopyButton data={data} />
      </div>
    </div>

    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm text-left">
        <thead className="text-xs text-slate-500 uppercase bg-slate-900/80">
          <tr>
            <th className="px-4 py-3">Cell Location</th>
            <th className="px-4 py-3 text-center">Distance</th>
            <th className="px-4 py-3 text-center">Found</th>
            <th className="px-4 py-3 text-center">Rank</th>
            <th className="px-4 py-3 text-center">Results</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800 bg-slate-900/30">
          {data.cells?.map((cell: any, idx: number) => (
            <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
              <td className="px-4 py-3 font-mono text-xs text-slate-300">{cell.lat.toFixed(4)}, {cell.lng.toFixed(4)}</td>
              <td className="px-4 py-3 text-center text-slate-400">{cell.distance_km} km</td>
              <td className="px-4 py-3 text-center">
                {cell.found ? (
                  <span className="text-emerald-400 font-semibold">Yes</span>
                ) : (
                  <span className="text-red-400 font-semibold">No</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                {cell.found ? <span className="font-bold text-slate-200">#{cell.rank}</span> : <span className="text-slate-600">-</span>}
              </td>
              <td className="px-4 py-3 text-center text-slate-400">{cell.result_count || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const PerspectiveSearchRenderer = ({ data }: { data: any }) => (
  <div className="space-y-4">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-pink-400" /> Perspective Search
        </h3>
        <p className="text-sm text-slate-400 mt-1">
          Keyword: <span className="text-slate-200 font-medium">"{data.keyword_text}"</span>
        </p>
      </div>
      <CopyButton data={data} />
    </div>

    <div className="grid gap-3">
      {data.cells?.map((cell: any, idx: number) => (
        <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="font-medium text-slate-200">{cell.location_name || `Point ${idx + 1}`}</div>
            <div className="text-xs font-mono text-slate-500">{cell.latitude}, {cell.longitude}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className={`text-lg font-bold ${cell.found ? 'text-emerald-400' : 'text-red-400'}`}>
                {cell.found ? `#${cell.rank}` : 'Not Found'}
              </div>
              <div className="text-[10px] text-slate-500 uppercase">Rank</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-slate-200">{cell.result_count || 0}</div>
              <div className="text-[10px] text-slate-500 uppercase">Results</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const TechnicalHealthRenderer = ({ data }: { data: any }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <Globe className="h-5 w-5 text-cyan-400" /> Technical Health
        </h3>
        <a href={data.audited_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline flex items-center gap-1 mt-1">
          {data.audited_url} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <ScoreBadge score={data.score} label={data.score_label} />
    </div>

    <div className="space-y-4">
      {data.sub_scores?.map((sub: any, idx: number) => (
        <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-200 capitalize">{sub.key?.replace('_', ' ')}</h4>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                sub.label === 'Critical' ? 'bg-red-500/10 text-red-400' :
                sub.label === 'Poor' ? 'bg-orange-500/10 text-orange-400' :
                sub.label === 'Fair' ? 'bg-yellow-500/10 text-yellow-400' :
                'bg-emerald-500/10 text-emerald-400'
              }`}>
                {sub.label || 'N/A'}
              </span>
              <span className="text-lg font-bold text-slate-200">{sub.score ?? '-'}</span>
            </div>
          </div>
          {Array.isArray(sub.findings) && sub.findings.length > 0 && (
            <ul className="space-y-1.5">
              {sub.findings.map((finding: any, i: number) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-500 shrink-0" />
                  <SafeValue value={finding} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
    <div className="flex justify-end pt-2">
      <CopyButton data={data} />
    </div>
  </div>
);

// Safely render any value to prevent React Error #31
export const SafeValue = ({ value }: { value: any }) => {
  if (value === null || value === undefined) return <span className="text-slate-500 italic">null</span>;
  if (typeof value === 'boolean') return <span className="text-purple-400">{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'number') return <span className="text-orange-400">{value}</span>;
  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">{value}</a>;
    }
    return <span className="text-slate-200 break-all">{value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-500">[]</span>;
    if (value.every(v => typeof v !== 'object' || v === null)) {
      return <span className="text-slate-300">{value.map(String).join(', ')}</span>;
    }
    return <span className="text-slate-400 text-xs font-mono bg-slate-800/50 px-1.5 py-0.5 rounded">Array ({value.length} items)</span>;
  }
  if (typeof value === 'object') {
    return <span className="text-slate-400 text-xs font-mono bg-slate-800/50 px-1.5 py-0.5 rounded break-all">{JSON.stringify(value)}</span>;
  }
  return <span className="text-slate-300">{String(value)}</span>;
};

// Fallback for blocks without a dedicated renderer (Content Quality, AEO, or unknown)
export const GenericBlockRenderer = ({ data }: { data: any }) => {
  const [showRaw, setShowRaw] = useState(false);
  
  const recommendations = Array.isArray(data.recommendations) ? data.recommendations : [];

  // Safely extract only primitive top-level keys
  const metrics = Object.entries(data).filter(([, v]) => 
    v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-100 capitalize flex items-center gap-2">
          <Globe className="h-5 w-5 text-slate-400" /> {data.block?.replace('_', ' ') || 'Block Data'}
        </h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowRaw(!showRaw)}
            className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
          >
            {showRaw ? 'Structured' : 'Raw JSON'}
          </button>
          <CopyButton data={data} />
        </div>
      </div>

      {showRaw ? (
        <pre className="text-[11px] leading-relaxed text-slate-300 overflow-auto whitespace-pre font-mono max-h-[60vh] p-4 bg-slate-900/80 rounded-lg border border-slate-800/80 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <div className="space-y-4">
          {recommendations.length > 0 && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 space-y-2">
              <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Recommendations</h4>
              {recommendations.map((rec: any, idx: number) => (
                <div key={idx} className="flex gap-3 text-sm">
                  <AlertCircle className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-200">{rec.title || 'Recommendation'}</div>
                    <div className="text-slate-400 text-xs mt-0.5">{rec.description || ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {metrics.map(([key, value]) => (
              <div key={key} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</div>
                <div className="text-sm font-medium break-all">
                  <SafeValue value={value} />
                </div>
              </div>
            ))}
          </div>
          
          {/* Show a hint for complex nested data that was safely hidden */}
          {Object.entries(data).some(([, v]) => typeof v === 'object' && v !== null && !Array.isArray(v)) && (
            <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-800/50">
              Complex nested data is available in the <button onClick={() => setShowRaw(true)} className="text-blue-400 hover:underline">Raw JSON</button> view.
            </div>
          )}
        </div>
      )}
    </div>
  );
};